/**
 * Calendar-date helpers (badge UAT F15 / Qwen audit issue 3): JHA dates are
 * CALENDAR dates stored as 'YYYY-MM-DD'. Constructing Date(...) from that
 * string yields UTC midnight, which renders as the PREVIOUS day in
 * America/Chicago-like timezones. Never timezone-round-trip a calendar date.
 *
 * BOUNDARY (owner decision D1, s4-design-pass 2026-10-15 §2A): this module is
 * the LOCAL-day contract (rendering + `<input type="date">` defaults +
 * local-day comparisons for the JHA/DOT surfaces). The incident/report
 * surfaces use the SEPARATE `./utcDay.ts` (UTC calendar-day parsing/format).
 * Do NOT merge the two — the contracts genuinely differ.
 */
export function formatCalendarDate(
  iso: string | undefined | null,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  if (opts) {
    // Build a local-noon Date so any locale formatting stays on the same day.
    return new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0).toLocaleDateString('en-US', opts);
  }
  return `${Number(mo)}/${Number(d)}/${Number(y)}`;
}

/**
 * Cash hardening (wd closing loop 2026-10-05): the value for `<input
 * type="date">` defaults — the user's LOCAL calendar day. The common
 * `new Date().toISOString().split('T')[0]` idiom returns the UTC day, which
 * shows TOMORROW's date to a US user filling a form in the evening (and
 * stamps compliance records on the wrong local day).
 */
export function toLocalDateInputValue(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * LUNA18-DOT-003: does a date-only shift/assignment record (stored as UTC
 * midnight of the picked calendar day) belong to the user's LOCAL calendar
 * day? The old UTC-only comparison hid same-day assignments from users in
 * UTC-negative zones after UTC midnight (Indiana evening: local Oct 9 = UTC
 * Oct 10). The recorded calendar day must equal the local calendar day of
 * `now`, evaluated in the user's timezone (device-local when omitted).
 */
export function isAssignmentForLocalDay(shiftDate: string | Date, now: Date = new Date(), timeZone?: string): boolean {
  const recorded = /^(\d{4})-(\d{2})-(\d{2})/.exec(
    typeof shiftDate === 'string' ? shiftDate : new Date(shiftDate).toISOString(),
  );
  if (!recorded) return false;
  const localDay = timeZone
    ? new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
    : toLocalDateInputValue(now);
  return `${recorded[1]}-${recorded[2]}-${recorded[3]}` === localDay;
}
